import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@c2fo/liquidity';
import PageBottomPortal from '@/enterprise/components/PageBottomPortal/PageBottomPortal';
import { useReporting } from '@/reporting';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import NavigationBar from './NavigationBar';
import { useOfferBuilderContext } from './OfferBuilderContext';
import OfferSummary from './OfferSummary';

const CustomerSelectLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEnterpriseEvent } = useReporting();
  const {
    dispatch,
    state: { builderVariant, error, selected, disableNavigation, submissionComplete },
  } = useOfferBuilderContext();

  // reset state if offer submit is complete
  useEffect(() => {
    if (submissionComplete) {
      dispatch({ type: 'RESET' });
    }
  }, [submissionComplete, dispatch]);

  const handleNextClick = () => {
    if (selected.length === 0) {
      return dispatch({ type: 'SET_ERROR', payload: { type: 'NO_TAKER_MARKETS_SELECTED' } });
    }

    trackEnterpriseEvent('offer-builder::step::completed', {
      step: 'customer-selection',
      customersSelected: selected.length,
      builderVariant,
      flow: 'stepped',
    });

    navigate(asEnterpriseLink('build-offer/frequency'));
  };

  return (
    <>
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="lg:w-7/12">{children}</div>
        <div className="flex-1">
          <div className="lg:sticky lg:top-24">
            <div className="mb-2 text-lg font-medium">{t('builder.offerSummary')}</div>
            {error?.type === 'NO_TAKER_MARKETS_SELECTED' && (
              <div className="mb-2">
                <Alert
                  description={t('builder.noTakerMarketsSelected')}
                  icon={false}
                  full
                  type="error"
                  variant="outlined"
                />
              </div>
            )}
            <div className="rounded-lg bg-deepBlue-50 p-2">
              <OfferSummary step="customer-select" />
            </div>
          </div>
        </div>
      </div>
      {!disableNavigation && (
        <PageBottomPortal>
          <NavigationBar>
            <div className="flex items-center justify-end">
              <Button
                className="w-full md:w-fit"
                data-intercom-target={'offerBuilderNext' satisfies IntercomDataTargetNames}
                onClick={handleNextClick}
              >
                {t('core.next')}
              </Button>
            </div>
          </NavigationBar>
        </PageBottomPortal>
      )}
    </>
  );
};

export default CustomerSelectLayout;
